import { Box, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { loadFonts } from 'hooks/useFont';
import { AutoBrandFont } from 'libs/e1api/autoBrand';
import { useTranslation } from 'react-i18next';

import { hasBrandFonts } from './AutoBrand.helpers';
import { AutoBrandProps, AutoFontProps, BrandContainerProps, FontTextProps } from './AutoBrand.types';
import { Logo } from './Logo';
import Palette from './Palette';

const BrandContainer = ({ children, heading }: BrandContainerProps) => {
  return (
    <Box bg='white' borderRadius='lg' w='100%' paddingX={4} paddingY={5} boxShadow='sm'>
      <Heading textTransform='uppercase' fontWeight='medium' color='font.300' fontSize='xs' pb={3}>
        {heading}
      </Heading>
      {children}
    </Box>
  );
};

const Brands = ({ brands }: AutoBrandProps) => {
  const { t } = useTranslation('auto_brand', { useSuspense: false, keyPrefix: 'modals.autoBrand' });

  return (
    <Flex minW='22rem' minH='32rem' flexDirection='column' gap={4} padding={6} overflow='auto'>
      {brands?.logos && (
        <BrandContainer heading={t('brandLogos')}>
          <SimpleGrid maxHeight='12.5rem' overflowY='scroll' columns={3} spacingX={2} spacingY={3}>
            {brands.logos.map((logo, key) => (
              <Logo key={key} logo={logo} />
            ))}
          </SimpleGrid>
        </BrandContainer>
      )}

      {brands?.colors && (
        <BrandContainer heading={t('brandColors')}>
          <Flex>
            <Palette palettes={brands?.colors} />
          </Flex>
        </BrandContainer>
      )}

      {hasBrandFonts(brands?.fonts) && (
        <BrandContainer heading={t('brandFonts')}>
          <Flex>
            <AutoBrandFonts fonts={brands!.fonts} />
          </Flex>
        </BrandContainer>
      )}
    </Flex>
  );
};

const AutoBrandFonts = ({ fonts }: { fonts: AutoBrandFont[] }) => {
  const title = fonts.find((f) => f.type === 'title' && f.isVenngageFont);
  const text = fonts.find((f) => f.type === 'text' && f.isVenngageFont);

  // load the fonts in case they are not loaded
  if (title) loadFonts([title.fontFamily]);
  if (text) loadFonts([text.fontFamily]);

  return <AutoFonts title={title?.fontFamily} heading={text?.fontFamily} />;
};

const FontText = ({ children, fontSize, fontFamily, fontWeight }: FontTextProps) => (
  <Text
    lineHeight='150%'
    whiteSpace='nowrap'
    textOverflow='ellipsis'
    overflow='hidden'
    fontWeight={fontWeight || 'bold'}
    fontSize={fontSize}
    fontFamily={fontFamily}
  >
    {children}
  </Text>
);

export const AutoFonts = ({ title, heading }: AutoFontProps) => {
  return (
    <Box position='relative' textAlign='left'>
      {title && (
        <FontText fontSize='lg' fontFamily={title}>
          {title}
        </FontText>
      )}
      {heading && (
        <FontText fontSize='sm' fontFamily={heading} fontWeight='normal'>
          {heading}
        </FontText>
      )}
    </Box>
  );
};

export default Brands;
